import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Grid,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
  withStyles,
  FormGroup,
} from "@material-ui/core";
import TextField from "../../../../../../Component/Forms/TextField";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import InputLabel from "@material-ui/core/InputLabel";
import ModalDialogContext from "../../../../../../Context/ModalDialog/ModalDialogContext";
import LoadingIndicator from "../../../../../../Component/LoadingIndicator";
import { processPermissionState } from "./processPermissionState";
import { updateRecordAccessRule } from "../../../../../../API/AccessControl";
import DisplayPermissions from "../AddRule/displayPermissions";
import DisplayUserGroups from "../AddRule/displayUserGroups";
import DisplayUserLocations from "../AddRule/displayUserLocations";
import Select from "@jll/select";

import styles from "./styles";
import { useTranslation } from "react-i18next";
import { recordTypes } from "../../../../../../API/config";
import EditRulesButton from "./editRulesButton";
import { getPermissionsdataforDbFromScratch } from "../AddRule/processPermissionForDb";
import { defaultPermissionsofRules } from "../../defaultPermissionsofRules";
import CustomButton from "../../../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const renderLoadingIndicator = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // height: "70vh",
      }}
    >
      <LoadingIndicator />
    </div>
  );
};

const ViewRule = (props) => {
  const {
    open,
    onclose,
    rule,
    entityCategories,
    entitySubCategories,
    geographiesData,
    countriesDataForAccessRole,
    reloadUiAfterSubmit,
    processSequencesForAccessRole,
    allUserGroups,
  } = props;
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(open);
  const [name, setName] = useState();
  const [description, setDescription] = useState();
  const [userGroups, setUserGroups] = useState([]);
  const [locations, setLocations] = useState({});
  const [permissions, setPermissions] = useState({});
  const [editModeOn, setEditModeOn] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [condForNonEdit, setCondForNonEdit] = useState();
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const { showErrorMessage, showLoadingMessage, showMessage } =
    useContext(ModalDialogContext);

  useEffect(() => {
    if (open) {
      processAddressData();
    }
  }, [open]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setName();
    setDescription();
    setUserGroups([]);
    setLocations({});
    setPermissions({});
    if (onclose) {
      onclose("cancel");
    }
  };

  const processAddressData = useCallback(() => {
    setIsDialogOpen(true);
    const {
      name,
      description,
      userGroups = [],
      locations,
      permissions,
    } = rule || {};
    setName(name);
    setDescription(description);
    setUserGroups(userGroups);
    setLocations(locations);
    const permissionsSkeleton = defaultPermissionsofRules(
      entityCategories,
      entitySubCategories,
      geographiesData,
      countriesDataForAccessRole,
      processSequencesForAccessRole
    );
    let { newPermission, defaultPermissions } = processPermissionState(
      permissions,
      permissionsSkeleton
    );
    setCondForNonEdit(newPermission);
    setPermissions(JSON.parse(JSON.stringify(defaultPermissions)) || {});
  }, [
    rule,
    countriesDataForAccessRole,
    entityCategories,
    entitySubCategories,
    geographiesData,
    processSequencesForAccessRole,
    allUserGroups,
  ]);

  const handleChangeInRules = useCallback(
    (event, fieldName, actionName, recordType) => {
      if (fieldName === "permissions") {
        event.persist();
        const checkedStatus = event.target.checked;
        setPermissions((prevVal) => ({
          ...prevVal,
          [recordType]: {
            ...prevVal[recordType],
            [actionName]: {
              ...prevVal[recordType][actionName],
              accessStatus: checkedStatus,
            },
          },
        }));
      }
    },
    [setPermissions]
  );

  const isValidForm = () => {
    const errors = {};
    let hasError = false;
    if (!name || typeof name !== "string" || (name || "").trim().length < 1) {
      errors["name"] = "should not be empty";
      hasError = true;
    }
    if (
      !description ||
      typeof description !== "string" ||
      (description || "").trim().length < 1
    ) {
      errors["description"] = "should not be empty";
      hasError = true;
    }

    if (hasError) {
      setFormErrors(errors);
    }
    return hasError;
  };

  const submitEditedData = async () => {
    try {
      setFormErrors({});
      const invalidEditedForm = isValidForm();
      if (invalidEditedForm) return;
      showLoadingMessage();
      const permissionsDataForDb =
        getPermissionsdataforDbFromScratch(permissions);
      const updatedAccessRule = {
        id: rule.id,
        name,
        description,
        userGroups: JSON.stringify(selectedUserGroups.map((ug) => ug.value)),
        permissions: JSON.stringify(permissionsDataForDb),
      };
      await updateRecordAccessRule(updatedAccessRule);
      showMessage({
        message: t("messages.accessControl.ruleUpdated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
    handleClose();
    reloadUiAfterSubmit();
  };

  const handleChangeInName = (value) => {
    setName(value);
  };

  const handleChangeInDesc = (value) => {
    setDescription(value);
  };

  const changeInDefaultPermissions = useCallback(
    (recordTypeName, actionName, updatePermission) => {
      setPermissions((prevVal) => ({
        ...prevVal,
        [recordTypeName]: {
          ...prevVal[recordTypeName],
          [actionName]: {
            ...prevVal[recordTypeName][actionName],
            conditions: updatePermission,
          },
        },
      }));
    },
    [setPermissions]
  );

  const toggleEditButton = useCallback(() => {
    setSelectedUserGroups(
      allUserGroups.filter((sg) => {
        return Array.isArray(userGroups) && userGroups.indexOf(sg.label) >= 0;
      })
    );
    setEditModeOn((prevVal) => !prevVal);
  }, [setEditModeOn, setSelectedUserGroups, allUserGroups, userGroups]);

  const handleChangeInSecuGroups = (e) => {
    if (e) {
      setSelectedUserGroups(e);
    } else {
      setSelectedUserGroups([]);
    }
  };

  const displayConditions = useCallback((conditions) => {
    let {
      //onlyProvidedUrns,
      confidential,
      checkTypes,
      cancelledCheck,
      riskFactors,
      scoringStatuses,
      ownerInSameGoup,
      onlyIfOwner,
      onlyUserCountry,
      countryIds = [],
      geographyId,
      processStates,
      entityCategories,
      entitySubCategories,
    } = conditions || {};
    countryIds = countriesDataForAccessRole
      .filter((country) => countryIds.indexOf(country.value) >= 0)
      .map((c) => c.label);

    return (
      <Grid container justify="center">
        <Grid item>
          <ul className={classes.listItem}>
            {confidential !== undefined && (
              <li alignItems="center" key={"confidential"}>
                {confidential
                  ? t("labels.accessControl.rule.conditions.confidential")
                  : t("labels.accessControl.rule.conditions.notConfidential")}
              </li>
            )}
            {checkTypes && checkTypes.length > 0 && (
              <li alignItems="center" key={"checkTypes"}>
                {t("labels.accessControl.rule.conditions.ckeckType", {
                  checkTypes: checkTypes.join(` ${t("labels.common.or")} `),
                })}
              </li>
            )}
            {cancelledCheck !== undefined && (
              <li alignItems="center" key={"cancelledCheck"}>
                {cancelledCheck
                  ? t("labels.accessControl.rule.conditions.cancelledCheck")
                  : t("labels.accessControl.rule.conditions.notCancelledCheck")}
              </li>
            )}
            {riskFactors && riskFactors.length > 0 && (
              <li alignItems="center" key={"scores"}>
                {t("labels.accessControl.rule.conditions.riskFactors", {
                  scores: riskFactors.join(` ${t("labels.common.or")} `),
                })}
              </li>
            )}
            {scoringStatuses && scoringStatuses.length > 0 && (
              <li alignItems="center" key={"scoringStatuses"}>
                {t("labels.accessControl.rule.conditions.scoringStatuses", {
                  scoringStatuses: scoringStatuses.join(
                    ` ${t("labels.common.or")} `
                  ),
                })}
              </li>
            )}
            {ownerInSameGoup !== undefined && (
              <li alignItems="center" key={"ownerInSameGoup"}>
                {t("labels.accessControl.rule.conditions.ownerInSameGoup")}
              </li>
            )}
            {onlyIfOwner !== undefined && (
              <li alignItems="center" key={"onlyIfOwner"}>
                {t("labels.accessControl.rule.conditions.onlyIfOwner")}
              </li>
            )}
            {onlyUserCountry !== undefined && (
              <li alignItems="center" key={"onlyUserCountry"}>
                {t("labels.accessControl.rule.conditions.onlyUserCountry")}
              </li>
            )}
            {countryIds && countryIds.length > 0 && (
              <li alignItems="center" key={"countryIds"}>
                {t("labels.accessControl.rule.conditions.countryIds", {
                  countryIds: countryIds.join(` ${t("labels.common.or")} `),
                })}
              </li>
            )}
            {geographyId && geographyId.length > 0 && (
              <li alignItems="center" key={"geographyId"}>
                {t("labels.accessControl.rule.conditions.geographyId")}
              </li>
            )}
            {processStates && processStates.length > 0 && (
              <li alignItems="center" key={"processStates"}>
                {t("labels.accessControl.rule.conditions.processStates", {
                  processStates: processStates.join(
                    ` ${t("labels.common.or")} `
                  ),
                })}
              </li>
            )}
            {entityCategories && entityCategories.length > 0 && (
              <li alignItems="center" key={"entityCategories"}>
                {t("labels.accessControl.rule.conditions.entityCategories", {
                  entityCategories: entityCategories.join(
                    ` ${t("labels.common.or")} `
                  ),
                })}
              </li>
            )}
            {entitySubCategories && entitySubCategories.length > 0 && (
              <li alignItems="center" key={"entitySubCategories"}>
                {t("labels.accessControl.rule.conditions.entitySubCategories", {
                  entitySubCategories: entitySubCategories.join(
                    ` ${t("labels.common.or")} `
                  ),
                })}
              </li>
            )}
          </ul>
        </Grid>
      </Grid>
    );
  }, []);

  const renderViewForm = () => {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item sm={6} md={6} xs={12}>
          {editModeOn ? (
            <>
              <TextField
                label={t("labels.accessControl.rule.form.name")}
                required={true}
                type="text"
                initialValue={name}
                onChange={handleChangeInName}
              />
              {formErrors["name"] && (
                <div className={classes.errorText}>{formErrors["name"]}</div>
              )}
            </>
          ) : (
            <>
              <InputLabel className={classes.inputLabel}>
                {t("labels.accessControl.rule.form.name")}
              </InputLabel>
              <span> {name}</span>
            </>
          )}
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          {editModeOn ? (
            <>
              <TextField
                label={t("labels.accessControl.rule.form.description")}
                required={true}
                type="text"
                initialValue={description}
                onChange={handleChangeInDesc}
              />
              {formErrors["description"] && (
                <div className={classes.errorText}>
                  {formErrors["description"]}
                </div>
              )}
            </>
          ) : (
            <>
              <InputLabel className={classes.inputLabel}>
                {t("labels.accessControl.rule.form.description")}
              </InputLabel>
              <span> {description}</span>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.userGroups")}
          </InputLabel>

          {editModeOn ? (
            <FormGroup>
              <Select
                value={selectedUserGroups}
                options={allUserGroups}
                isMulti={true}
                placeholder={t("labels.common.select")}
                isSearchable={true}
                className={classes.multiSelector}
                onChange={handleChangeInSecuGroups}
                noOptionsText={t("messages.common.noMoreOptions")}
              />
            </FormGroup>
          ) : (
            <DisplayUserGroups userGroups={userGroups} />
          )}
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.locations")}
          </InputLabel>
          <DisplayUserLocations locations={locations} />
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.permissions")}
          </InputLabel>
          {permissions &&
            Object.keys(permissions).length > 0 &&
            Object.keys(recordTypes).map((rt) => (
              <DisplayPermissions
                key={rt}
                editModeOn={editModeOn}
                permissions={permissions}
                handleChange={handleChangeInRules}
                recordType={recordTypes[rt]}
                changeInDefaultPermissions={changeInDefaultPermissions}
                displayConditions={displayConditions}
                condForNonEdit={condForNonEdit}
              />
            ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        disableBackdropClick
        onClose={handleClose}
        aria-labelledby="add-document-modal"
        open={isDialogOpen}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="add-document-modal" onClose={handleClose}>
          <Typography className={classes.panelTitle}>
            {t("labels.accessControl.rules.viewRule")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderViewForm()}
        </DialogContent>

        <DialogActions>
          
          <CustomButton variant="secondary" onClick={handleClose}>
            {t("labels.common.close")}
          </CustomButton>
          <EditRulesButton
            isEditedMode={editModeOn}
            submitEditedData={submitEditedData}
            toggleEditMode={toggleEditButton}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewRule;
